import {Box, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';

import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import ArrTransferList, {
  KeyValue,
} from '../../common/arr-transfer-list/ArrTransferList';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';

import {RootState} from '../../../redux/store';
import {
  setIsAnalyticsActive,
  setIsRedactionPropagationActive,
  setAnalyticsComponentsSelected,
  setAnalyticsAutomationSelected,
  setIsGenerativeAIActive,
  setGenerativeAISelected,
} from '../../../redux/slice/projectFormSlice';

import './GeneralTab.scss';
import ArrModal from '../../common/arr-modal/ArrModal';
import {
  availableComponentList,
  baseAutomationList,
  displayedGenerativeAIList,
} from './projectUtility';
import {useGetClientData} from '../../../hooks/clients/useClients';

const ProjectAnalytics: React.FC = () => {
  const dispatch = useDispatch();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Modal state for Generative AI toggle confirmation
  const [showGenerativeAIModal, setShowGenerativeAIModal] = useState(false);
  const [pendingGenerativeAIState, setPendingGenerativeAIState] = useState<
    null | boolean
  >(null); // null = no pending action

  const {
    isAnalyticsActive,
    isRedactionPropagationActive,
    analyticsComponentsSelected = [],
    analyticsAutomationSelected = [],
    redactionPropagationDisable,
    type,
    isGenerativeAIActive,
    generativeAISelected = [],
    selectedClientId,
    selectedClient,
  } = useSelector((state: RootState) => state.projectForm);

  const {data: clientData} = useGetClientData(
    {
      entityId: selectedClientId ?? 0,
      entityCode: '',
      entityLabel: selectedClient ?? '',
    },
    !!selectedClientId
  );

  const isCheckbox2Checked = useMemo(() => {
    return (analyticsAutomationSelected ?? []).some((item) => item.id === 2);
  }, [analyticsAutomationSelected]);

  const displayedAutomationList = useMemo(() => {
    return baseAutomationList.map((item) => ({
      ...item,
      disabled: item.id === 3 && !isCheckbox2Checked,
    }));
  }, [isCheckbox2Checked]);

  useEffect(() => {
    if (type !== 'edit') {
      dispatch(setAnalyticsComponentsSelected(availableComponentList));
      dispatch(setAnalyticsAutomationSelected(baseAutomationList));
    }
  }, []);

  useEffect(() => {
    if (!isGenerativeAIActive && generativeAISelected.length > 0) {
      dispatch(setGenerativeAISelected([]));
    }
  }, [isGenerativeAIActive, generativeAISelected, dispatch]);

  const handleAnalyticsToggleChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    dispatch(setIsAnalyticsActive(checked));
    if (checked) {
      dispatch(setAnalyticsComponentsSelected(availableComponentList));
      dispatch(setAnalyticsAutomationSelected(baseAutomationList));
    } else {
      dispatch(setAnalyticsComponentsSelected([]));
      dispatch(setAnalyticsAutomationSelected([]));
    }
  };

  const handleRedactionToggleChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (!checked) {
      setShowConfirmModal(true);
      return;
    }

    if (!redactionPropagationDisable) {
      dispatch(setIsRedactionPropagationActive(true));
    }
  };

  const confirmDisableRedaction = () => {
    dispatch(setIsRedactionPropagationActive(false));
    setShowConfirmModal(false);
  };

  const cancelDisableRedaction = () => {
    setShowConfirmModal(false);
  };

  const handleAssignComponentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => {
    const isChecked = event.target.checked;
    const updated = isChecked
      ? [...analyticsComponentsSelected, field]
      : analyticsComponentsSelected.filter((item) => item.id !== field.id);

    dispatch(setAnalyticsComponentsSelected(updated));
    if (!isChecked) {
      dispatch(setAnalyticsAutomationSelected([]));
      dispatch(setIsAnalyticsActive(false));
    }
  };

  const handleAutomationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => {
    const isChecked = event.target.checked;
    if (analyticsComponentsSelected.length === 0) return;

    const isCheckbox2Checked = (analyticsAutomationSelected ?? []).some(
      (item) => item.id === 2
    );

    let updated = [...analyticsAutomationSelected];

    if (field.id === 3 && !isCheckbox2Checked && isChecked) {
      return;
    }

    if (isChecked) {
      if (!updated.some((item) => item.id === field.id)) {
        updated.push(field);
      }
    } else {
      if (field.id === 2) {
        updated = updated.filter((item) => item.id !== 2 && item.id !== 3);
      } else {
        updated = updated.filter((item) => item.id !== field.id);
      }
    }

    dispatch(setAnalyticsAutomationSelected(updated));
  };

  // Handler for Generative AI toggle (shows confirmation modal)
  const handleGenerativeAIToggleChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setPendingGenerativeAIState(checked);
    setShowGenerativeAIModal(true);
  };

  // Confirm/cancel handlers for Generative AI modal
  const confirmGenerativeAIModal = () => {
    if (pendingGenerativeAIState === true) {
      dispatch(setGenerativeAISelected(displayedGenerativeAIList));
    } else {
      dispatch(setGenerativeAISelected([]));
    }
    dispatch(setIsGenerativeAIActive(!!pendingGenerativeAIState));
    setShowGenerativeAIModal(false);
    setPendingGenerativeAIState(null);
  };

  const cancelGenerativeAIModal = () => {
    setShowGenerativeAIModal(false);
    setPendingGenerativeAIState(null);
  };

  const handleGenerativeAIListChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: KeyValue
  ) => {
    const isChecked = event.target.checked;

    let updated = [...generativeAISelected];

    if (isChecked) {
      if (!updated.some((item) => item.id === field.id)) {
        updated.push(field);
      }
    } else {
      updated = updated.filter((item) => item.id !== field.id);
    }
    if (updated?.length === 0) {
      dispatch(setIsGenerativeAIActive(false));
    }

    dispatch(setGenerativeAISelected(updated));
  };

  return (
    <Box className="general-tab-project-analytics">
      {/* Header */}
      <Box
        className={`project-analytics-container-header ${isAnalyticsActive ? '' : 'space-bottom'}`}
      >
        <ArrToggle
          disabled={false}
          checked={isAnalyticsActive}
          onChange={handleAnalyticsToggleChange}
          dataTestId="project-analysis-toggle"
        />
        <Typography
          className="typo-text subhead3_size"
          data-testid={`project-analytics-analytics`}
        >
          {t('projectManagement.createProjectModule.analytics')}
        </Typography>
      </Box>
      {isAnalyticsActive && (
        <Box className={`toggle-analytics-content space-bottom`}>
          <Box className="section-box component-section-box">
            <Box className="section-header">
              <Typography
                className="typo-text subhead5_size component-title"
                data-testid={`project-analytics-components`}
              >
                {t('projectManagement.createProjectModule.components')}
              </Typography>
              <ArrTooltipWrapper
                placement="right"
                trigger={<InfoIcon className="info-icon" />}
              >
                {t('projectManagement.createProjectModule.componentsTooltip')}
              </ArrTooltipWrapper>
            </Box>

            <Box className="component-transfer-list">
              <Typography
                className="typo-text body4_size component-subtitle"
                data-testid={`project-analytics-analytics-component-checkbox-text`}
              >
                {t(
                  'projectManagement.createProjectModule.analyticsComponentCheckboxText'
                )}
              </Typography>
              <ArrTransferList
                showCheckbox
                selectedList={analyticsComponentsSelected ?? []}
                displayedList={availableComponentList}
                onChange={handleAssignComponentChange}
                checkboxDataTestId="project-analytics-components-transfer-list-checkbox"
                showHeaderBorder={false}
              />
            </Box>
          </Box>

          {/* Automation Section */}
          <Box className="section-box">
            <Box className="section-header">
              <Typography
                className="typo-text subhead5_size component-title"
                data-testid={`project-analytics-automation`}
              >
                {t('projectManagement.createProjectModule.automation')}
              </Typography>
            </Box>
            <ArrTransferList
              showCheckbox
              selectedList={analyticsAutomationSelected ?? []}
              displayedList={displayedAutomationList}
              onChange={handleAutomationChange}
              checkboxDataTestId="project-analytics-automation-transfer-list-checkbox"
              endIcon={(item: KeyValue) => (
                <ArrTooltipWrapper
                  placement="right"
                  trigger={<InfoIcon className="info-icon" />}
                >
                  {item.id === 2
                    ? t('projectManagement.validation.tooltipText1')
                    : t('projectManagement.validation.tooltipText2')}
                </ArrTooltipWrapper>
              )}
              showHeaderBorder={false}
            />
          </Box>
        </Box>
      )}

      {/* Generative AI / AI Solutions Section */}
      {clientData?.entityModelData?.canUseGenAI && (
        <Box
          className={`generative-ai-container ${isGenerativeAIActive ? '' : 'space-bottom'}`}
        >
          <Box className="generative-ai-container-header">
            <ArrToggle
              disabled={false}
              checked={
                pendingGenerativeAIState !== null
                  ? pendingGenerativeAIState
                  : isGenerativeAIActive
              }
              onChange={handleGenerativeAIToggleChange}
              dataTestId="ai-continer-header-toggle"
            />
            <Typography
              className="typo-text subhead3_size"
              data-testid={`project-analytics-generative-ai`}
            >
              {t('projectManagement.createProjectModule.generativeAI')}
            </Typography>
          </Box>
          {isGenerativeAIActive && (
            <Box className="generative-ai-options">
              <ArrTransferList
                showCheckbox
                listItemContainerClass="generative-ai-list"
                selectedList={generativeAISelected ?? []}
                displayedList={displayedGenerativeAIList}
                onChange={handleGenerativeAIListChange}
                checkboxDataTestId="project-analytics-generative-ai-transfer-list-checkbox"
                showHeaderBorder={false}
              />
            </Box>
          )}
        </Box>
      )}
      {/* Redaction Section */}
      <Box className="redaction-container">
        <Box className="project-redaction-container-header">
          <ArrToggle
            disabled={redactionPropagationDisable}
            checked={isRedactionPropagationActive}
            onChange={handleRedactionToggleChange}
            dataTestId="redaction-container-header-toggle"
          />

          <Typography
            className="typo-text subhead3_size redaction-propagation-title"
            data-testid={`project-analytics-redaction-propagation`}
          >
            {t('projectManagement.createProjectModule.redactionPropagation')}
          </Typography>
          <ArrTooltipWrapper
            placement="right"
            trigger={<InfoIcon className="info-icon-small" />}
          >
            {t(
              'projectManagement.createProjectModule.redactionPropagationTooltip'
            )}
          </ArrTooltipWrapper>
        </Box>
      </Box>
      {showConfirmModal && (
        <ArrModal
          showModal={showConfirmModal}
          showClose={true}
          onClose={cancelDisableRedaction}
          headerTitle={t('projectManagement.createProjectModule.justToConfirm')}
          title={t('projectManagement.createProjectModule.redactionTitle')}
          description={t(
            'projectManagement.createProjectModule.redactionDescription'
          )}
          dataTestId="show-confirm-modal"
          buttons={[
            {
              id: '1',
              label: t('projectManagement.createProjectModule.btnCancel'),
              onClick: cancelDisableRedaction,
              variant: 'secondary',
              dataTestId: 'scm-cancel-button',
            },
            {
              id: '2',
              label: t('projectManagement.createProjectModule.btnDisable'),
              onClick: () => confirmDisableRedaction(),
              variant: 'primary',
              dataTestId: 'scm-confirm-button',
            },
          ]}
        />
      )}
      {/* Generative AI Confirmation Modal */}
      {showGenerativeAIModal && (
        <ArrModal
          showModal={showGenerativeAIModal}
          onClose={cancelGenerativeAIModal}
          title={
            pendingGenerativeAIState
              ? t(
                  'projectManagement.generativeAIConfirmationModal.enableConfirmationTitle'
                )
              : t(
                  'projectManagement.generativeAIConfirmationModal.disableConfirmationTitle'
                )
          }
          description={
            pendingGenerativeAIState
              ? t(
                  'projectManagement.generativeAIConfirmationModal.enableProjectConfirmationDescription'
                )
              : t(
                  'projectManagement.generativeAIConfirmationModal.disableConfirmationDescription'
                )
          }
          autoFocusCloseButton={true}
          dataTestId="generativeAI-modal"
          buttons={[
            {
              id: '1',
              label: t(
                'projectManagement.generativeAIConfirmationModal.btnCancel'
              ),
              onClick: cancelGenerativeAIModal,
              variant: 'secondary',
              dataTestId: 'generativeAI-modal-cancel-btn',
            },
            {
              id: '2',
              label: t(
                'projectManagement.generativeAIConfirmationModal.btnConfirm'
              ),
              onClick: confirmGenerativeAIModal,
              variant: 'primary',
              dataTestId: 'generativeAI-modal-close-btn',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default ProjectAnalytics;
